// frontend/src/components/finance/TransactionHistoryModal.js
//
// What this ledger line used to say, and who changed it.
//
// Before the audit log existed, an edit overwrote the row and the old values
// were gone. `updatedAt` moved, so the ledger could tell you a line had been
// touched — but not what changed, from what, or by whom, and `amount`, `type`
// and `date` are all editable. This is the panel that answers it.

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import API_URL from '../../api/baseURL';
import { Modal, Button, ErrorNote, Loading } from '../ui/Iparc';
import { signal } from '../../theme/iparc';

// French labels for the eight editable fields plus the settlement machinery.
const FIELD_LABELS = {
  type: 'Type',
  category: 'Catégorie',
  amount: 'Montant',
  reason: 'Motif',
  reference: 'Référence',
  vehicleId: 'Véhicule',
  date: 'Date',
  dueDate: 'Échéance',
  settlement: 'État',
};

const ACTION_LABELS = {
  update: 'Modification',
  settle: 'Règlement',
  split: 'Règlement partiel',
  create: 'Création',
  delete: 'Suppression',
};

const ACTION_TONES = {
  update: signal.warn,
  settle: signal.ok,
  split: signal.warn,
  create: '#8F877B',
  delete: signal.danger,
};

const VALUE_LABELS = {
  in: 'Entrée',
  out: 'Sortie',
  settled: 'Réglé',
  pending: 'En attente',
};

// An ISO date renders as a date; everything else as-is. A cleared field shows a
// dash rather than "null", which would read as a value someone typed.
function renderValue(v, field) {
  if (v === null || v === undefined || v === '') return '—';
  if (VALUE_LABELS[v]) return VALUE_LABELS[v];
  if (field === 'amount') return `${Number(v).toLocaleString('fr-FR')} F`;
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
    return new Date(v).toLocaleDateString('fr-FR');
  }
  return String(v);
}

export default function TransactionHistoryModal({ open, entry, token, onClose }) {
  const [entries, setEntries] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !entry?._id) return;
    let alive = true;
    setLoading(true);
    setError('');
    setEntries(null);
    axios.get(`${API_URL}/finance/transaction/${entry._id}/history`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => { if (alive) setEntries(res.data?.entries || []); })
      .catch((err) => {
        if (alive) setError(err.response?.data?.error || "Impossible de charger l'historique");
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, entry, token]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      subtitle="Caisse"
      title="Historique de l'écriture"
      footer={<Button onClick={onClose}>Fermer</Button>}
    >
      {loading && <Loading />}
      {error && <ErrorNote>{error}</ErrorNote>}

      {entries && entries.length === 0 && (
        <p className="text-[12.5px] text-[#8F877B]">
          Cette écriture n’a jamais été modifiée depuis sa création.
        </p>
      )}

      {entries && entries.length > 0 && (
        <ul className="flex flex-col gap-2.5">
          {entries.map((e) => {
            const tone = ACTION_TONES[e.action] || '#8F877B';
            return (
              <li key={e._id}
                  className="rounded-[12px] border border-[#241F1B] bg-[#141210] p-3">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-[11px] font-semibold uppercase tracking-[.12em]"
                        style={{ color: tone }}>
                    {ACTION_LABELS[e.action] || e.action}
                  </span>
                  <span className="text-[11px] text-[#6E665C] tabular-nums">
                    {new Date(e.createdAt).toLocaleString('fr-FR')}
                  </span>
                </div>

                <p className="mt-1 text-[11.5px] text-[#8F877B]">
                  par {e.actorName || 'utilisateur inconnu'}
                </p>

                {e.note && (
                  <p className="mt-1.5 text-[11.5px] text-[#C9C3B6]">{e.note}</p>
                )}

                {e.changes && e.changes.length > 0 && (
                  <ul className="mt-2 flex flex-col gap-1">
                    {e.changes.map((c, i) => (
                      <li key={`${c.field}-${i}`}
                          className="flex flex-wrap items-baseline gap-1.5 text-[11.5px]">
                        <span className="text-[#6E665C] min-w-[72px]">
                          {FIELD_LABELS[c.field] || c.field}
                        </span>
                        {/* The old value is the whole point, so it is the one
                            rendered plainly; the new value is what the row
                            already shows. */}
                        <span className="text-[#C9C3B6] line-through decoration-[#5C554C]
                                         font-['Space_Grotesk',sans-serif] tabular-nums">
                          {renderValue(c.from, c.field)}
                        </span>
                        <span className="text-[#5C554C]">→</span>
                        <span className="text-[#F4F1EA] font-['Space_Grotesk',sans-serif] tabular-nums">
                          {renderValue(c.to, c.field)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
}
